import { addMinutes } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { apiKeyBasedOnEnv } from '../components/meeting/utils/functions';
import { Meetingroom, UserMatch } from '../interfaces/interfaces';
import { SpeedDatingComponentInterface } from '../interfaces/pagebuilder';
import { setMatchHistory, setSpeeddatingState } from '../reduxToolkit/slices/commonSlice';
import { useSelector } from '../reduxToolkit/typedRedux';
import { endUserMatch, getMeeting, getMyUserMatchingsHistory, joinMeeting, toggleUserMatchHighlight } from '../services/services';
import { SPEEDDATINGSTATE } from '../utils/enums';

export function useMatching(settings?: SpeedDatingComponentInterface) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const event = useSelector(({ common }) => common.event);
  const user = useSelector(({ common }) => common.user);
  const audioInputDevice = useSelector(({ device }) => device.audioInputDevice);
  const videoInputDevice = useSelector(({ device }) => device.videoInputDevice);
  const speeddating = useSelector(({ common }) => common.speeddating);
  const pusherChannels = useSelector(({ common }) => common.pusherChannels);
  const registration = useSelector(({ common }) => common.eventRegistration);

  const [session, setSession] = useState<OT.Session>();
  const [publisher, setPublisher] = useState<OT.Publisher>();
  const [rating, setRating] = useState(0);
  const [meeting, setMeeting] = useState<Meetingroom>();
  const [nameOfPartner, setNameOfPartner] = useState<string>();
  const [camActive, setCamActive] = useState(true);

  const participantsRef = useRef<OT.Connection[]>([]);

  function getCountdownMinutes() {
    return addMinutes(new Date(), settings?.duration ?? 0);
  }

  async function setState(state: SPEEDDATINGSTATE) {
    if (speeddating.state === SPEEDDATINGSTATE.WAITING && state === SPEEDDATINGSTATE.INACTIVE) {
      try {
        registration && (await endUserMatch(registration?._id));
      } catch (error) {
        console.error(error);
      }
    }
    dispatch(setSpeeddatingState(state));
  }

  async function handleState(state: SPEEDDATINGSTATE) {
    switch (state) {
      case SPEEDDATINGSTATE.CONNECTING:
        start();
        break;
      case SPEEDDATINGSTATE.CONNECTED:
        break;
      case SPEEDDATINGSTATE.ENDING:
        await end();
        loadHistory();
        return;
      case SPEEDDATINGSTATE.INACTIVE:
        await end();
        break;
      case SPEEDDATINGSTATE.WAITING:
        break;
    }
  }

  async function rateCall(value: number) {
    setRating(value);
    //TODO add rating api
  }

  async function start() {
    if (!speeddating.meetingId) {
      toast.error('No meeting id available');
    }

    await _connectAndPublish(speeddating.meetingId!);
  }

  async function end() {
    if (session && session.connection) {
      session.disconnect();
    } else _reset();
  }

  function toggleCam() {
    publisher?.publishVideo(!camActive);
    setCamActive(!camActive);
  }

  async function loadHistory() {
    event &&
      registration &&
      getMyUserMatchingsHistory(registration!._id).then((matches) => {
        dispatch(setMatchHistory(matches));
      });
  }

  async function toggleHighlight(match: UserMatch) {
    await toggleUserMatchHighlight(match._id, registration!._id);
  }

  function _reset() {
    setSession(undefined);
    setPublisher(undefined);
    setRating(0);
    setMeeting(undefined);
    setNameOfPartner('');
    setCamActive(true);
    if (speeddating.state === SPEEDDATINGSTATE.ENDING) {
      setState(SPEEDDATINGSTATE.WAITING);
    }
  }

  async function _connectAndPublish(meetingId: string) {
    console.log('connectAndPublish', meetingId);
    const meeting = await getMeeting(meetingId);
    const apiKey = apiKeyBasedOnEnv(false);
    const initializedSession = OT.initSession(apiKey, meeting.vonageSessionId);
    const joinResponse = await joinMeeting(meeting?._id!, user?.name!);

    try {
      initializedSession.connect(joinResponse.data?.token, (error) => {
        if (error) {
          toast.error(t('meeting:errors.connect'));
          console.error(error);
        } else {
          console.log('Successfully connected to session');
          const layoutDiv = document.getElementById('speedDatingLayout');
          try {
            const initializedPublisher = OT.initPublisher(
              layoutDiv!,
              {
                name: user?.name,
                publishAudio: true,
                publishVideo: camActive,
                audioSource: audioInputDevice?.deviceId,
                videoSource: videoInputDevice?.deviceId,
                insertMode: 'append',
                resolution: '1920x1080',
                width: '50%',
                height: '100%',
                fitMode: 'contain',
                mirror: true,
              },
              (error) => {
                if (error) {
                  console.error(error);
                }
              }
            );
            setPublisher(initializedPublisher);
            initializedSession.publish(initializedPublisher, (error) => {
              if (error) console.log('Publishing to initialized session failed. Potentially hung up call too early.');
              else {
                console.log('Successfully published in session');
                dispatch(setSpeeddatingState(SPEEDDATINGSTATE.CONNECTED));
              }
            });
          } catch (err) {
            console.log('error while initilializing publisher');
          }
        }
      });
    } catch (err) {
      console.log('error while connecting to session', err);
    }

    initializedSession.on('sessionDisconnected', () => {
      console.log('Calling reset after initialized session is destroyed.');
      _reset();
    });

    setMeeting(meeting);
    setSession(initializedSession);
    _setupSessionEvents(initializedSession);

    // pusher.setupMeetingChannel(meeting._id);
  }

  function _setupSessionEvents(session: OT.Session) {
    session?.on('connectionCreated', (event: any) => {
      console.log('connectionCreated');
      try {
        const clone = [...participantsRef.current];
        clone.push(event.connection);
        participantsRef.current = clone;
        // dispatch(setCallParticipants(participantsRef.current));

        const isNotHimself = participantsRef.current.find((i) => i.connectionId !== session?.connection?.connectionId);
        if (isNotHimself) {
          setNameOfPartner(JSON.parse(isNotHimself?.data!).username);
        }
      } catch (err) {
        console.error(err);
        toast.error(t('meeting:errors.connection-created'));
      }
    });

    session?.on('connectionDestroyed', (event: any) => {
      console.log('connectionDestroyed');
      try {
        const index = participantsRef.current.indexOf(participantsRef.current.find((i) => i.connectionId === event.connection.id)!);
        const clone = [...participantsRef.current];
        participantsRef.current = clone.splice(index, 0);

        setState(SPEEDDATINGSTATE.ENDING);
        // dispatch(setCallParticipants(participantsRef.current));

        // This event is not triggered when the user himself ends the call
        // Hence we can use this to change the callingstate
        // dispatch(setCallingState(CALLINGSTATE.ENDED));
      } catch (err) {
        console.error(err);
        toast.error(t('meeting:errors.connection-destroyed'));
      }
    });

    session?.on('sessionDisconnected', (event: any) => {
      console.log('sessionDisconnected');

      try {
        toast(t('meeting:on-site-call.ended'));
      } catch (err) {
        console.error(err);
        toast.error(t('meeting:errors.session-disconnected'));
      }
    });

    // session?.on('signal', function (data) {
    //   console.log('signal', data);

    //   try {
    //   } catch (err) {
    //     console.error(err);
    //   }
    // });

    session?.on('streamCreated', function (event: any) {
      console.log('streamCreated');
      try {
        const layoutContainer = document.getElementById('speedDatingLayout');
        session?.subscribe(
          event.stream,
          layoutContainer!,
          {
            insertMode: 'append',
            width: '50%',
            height: '100%',
            subscribeToAudio: true,
            subscribeToVideo: true,
          },
          (error) => {
            if (error) console.error(error);
          }
        );
      } catch (err) {
        console.error(err);
        toast.error(t('meeting:errors.stream-created'));
      }
    });
  }

  return { getCountdownMinutes, handleState, rateCall, toggleCam, toggleHighlight, loadHistory, rating, camActive, setState, publisher };
}
